import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  PlusIcon,
  DocumentDuplicateIcon,
  ArrowLeftIcon,
  ChartBarIcon,
  UserGroupIcon,
  EyeIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { fetchMyOpportunities, reviveOpportunity, closeOpportunity, fetchMyApplications } from '../store/slices/opportunitiesSlice';
import Button from '../components/common/Button';
import ManagementOpportunityCard from '../components/opportunities/ManagementOpportunityCard';
import OpportunityCard from '../components/opportunities/OpportunityCard';

const MyOpportunitiesPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'active';
  
  const { myOpportunities, myApplications, loading } = useAppSelector((state) => state.opportunities);
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    dispatch(fetchMyOpportunities('all')); // Fetch all to calculate stats correctly
    dispatch(fetchMyApplications());
  }, [dispatch]);

  // Calculate Stats
  const stats = useMemo(() => {
    return {
      totalActive: myOpportunities.filter(o => o.status === 'active').length,
      totalApplications: myOpportunities.reduce((acc, curr) => acc + curr.application_count, 0),
      totalViews: myOpportunities.reduce((acc, curr) => acc + curr.view_count, 0),
      myApplicationsCount: myApplications.length
    };
  }, [myOpportunities, myApplications]);

  const tabs = [
    { id: 'active', label: 'Active', count: myOpportunities.filter(o => o.status === 'active').length },
    { id: 'draft', label: 'Drafts', count: myOpportunities.filter(o => o.status === 'draft').length },
    { id: 'closed', label: 'Closed', count: myOpportunities.filter(o => o.status === 'closed').length },
    ...(user?.user_type !== 'hirer' ? [{ id: 'applied', label: 'Applied', count: myApplications.length }] : []),
    { id: 'all', label: 'All', count: myOpportunities.length },
  ];

  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId });
  };

  const filteredOpportunities = useMemo(() => {
    if (activeTab === 'all') return myOpportunities;
    if (activeTab === 'applied') return []; // Handled separately
    return myOpportunities.filter(o => o.status === activeTab);
  }, [myOpportunities, activeTab]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-12">
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <button
              onClick={() => navigate(user?.user_type === 'hirer' ? '/explore' : '/opportunities')}
              className="flex items-center space-x-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mb-2"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              <span>{user?.user_type === 'hirer' ? 'Back to Explore' : 'Back to Browse'}</span>
            </button>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {user?.user_type === 'hirer' ? 'My Requirements' : 'My Dashboard'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage your {user?.user_type === 'hirer' ? 'requirements' : 'opportunities'} and track performance
            </p>
          </div>
          
          <Link to="/opportunities/post" className="shrink-0">
            <Button variant="primary" className="flex items-center space-x-2 shadow-sm whitespace-nowrap">
              <PlusIcon className="h-5 w-5" />
              <span>{user?.user_type === 'hirer' ? 'Post New Requirement' : 'Post New Opportunity'}</span>
            </Button>
          </Link>
        </div>

        {/* Stats Overview */}
        <div className={`grid grid-cols-1 ${user?.user_type === 'hirer' ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-6 mb-8`}>
          <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-800">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                <ChartBarIcon className="h-6 w-6 text-teal-600 dark:text-teal-400" />
              </div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Posts</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.totalActive}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">Currently live {user?.user_type === 'hirer' ? 'requirements' : 'opportunities'}</div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-800">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <UserGroupIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Applications</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.totalApplications}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">Received on your posts</div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-800">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <EyeIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Views</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.totalViews}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">Lifetime visibility</div>
          </div>

          {user?.user_type !== 'hirer' && (
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                  <BriefcaseIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">My Applications</span>
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.myApplicationsCount}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">Opportunities applied to</div>
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 min-h-[500px]">
          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-800 px-6">
            <nav className="flex space-x-8 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors relative whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-700'
                  }`}
                >
                  {tab.label}
                  <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs ${
                    activeTab === tab.id
                      ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* List */}
          <div className="p-6">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="animate-pulse bg-gray-50 dark:bg-gray-800 rounded-xl p-6 h-64 border border-gray-100 dark:border-gray-700"></div>
                ))}
              </div>
            ) : activeTab === 'applied' ? (
              // Applied Opportunities View
              myApplications.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                  {myApplications.map((application) => (
                    application.opportunity ? (
                      <motion.div
                        key={application.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <OpportunityCard 
                          opportunity={application.opportunity}
                          userApplication={application}
                        />
                      </motion.div>
                    ) : null
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-20 h-20 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                    <BriefcaseIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No applications yet
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-sm mx-auto">
                    Start exploring opportunities and apply to the ones that match your skills!
                  </p>
                  <Link to="/opportunities">
                    <Button variant="primary">
                      Browse Opportunities
                    </Button>
                  </Link>
                </div>
              )
            ) : filteredOpportunities.length > 0 ? (
              // My Posted Opportunities View
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
                {filteredOpportunities.map((opportunity) => (
                  <motion.div
                    key={opportunity.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ManagementOpportunityCard 
                      opportunity={opportunity}
                      onClose={(id) => {
                        dispatch(closeOpportunity(id));
                      }}
                      onRevive={(id) => {
                        dispatch(reviveOpportunity(id));
                      }}
                    />
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                  <DocumentDuplicateIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No {activeTab === 'all' ? '' : activeTab} {user?.user_type === 'hirer' ? 'requirements' : 'opportunities'} found
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-sm mx-auto">
                  {activeTab === 'draft' 
                    ? 'Drafts are a great way to prepare your posts before going live.'
                    : `Create a ${user?.user_type === 'hirer' ? 'requirement' : 'opportunity'} to start connecting with talent.`
                  }
                </p>
                <Link to="/opportunities/post">
                  <Button variant="primary">
                    {user?.user_type === 'hirer' ? 'Post Requirement' : 'Post Opportunity'}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyOpportunitiesPage;
